import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import { setLoader } from "../../features/FullPageLoader/FullPageLoaderSlice";

export const useAddEmployee = (props) => {
  const { state } = props;
  const { authData } = useSelector((state) => state.loginSlice);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Employee State Fields
  const [iEmployeeId, setIEmployeeId] = useState("");
  const [sEmployeeCode, setSEmployeeCode] = useState("");
  const [sName, setSName] = useState("");
  const [sDesignation, setSDesignation] = useState("");
  const [sBranch, setSBranch] = useState("");
  const [sState, setSState] = useState("");
  const [sDistrict, setSDistrict] = useState("");
  const [bEnabled, setBEnabled] = useState(true); // Initial state is true

  // Error States
  const [codeError, setCodeError] = useState("");
  const [nameError, setNameError] = useState("");
  const [response, setResponse] = useState("");
  const [openUpdateAlert, setOpenUpdateAlert] = useState(false);

  // Handlers for Alert Modal
  const handleClose = () => {
    setOpenUpdateAlert(false);
    if (response.includes("success")) { // Check if the response indicates success
      navigate("/employees", { replace: true });
    }
    setResponse("");
  };

  useEffect(() => {
    if (response !== "") {
      setOpenUpdateAlert(true);
    }
  }, [response]);

  // Validation Functions
  const isValidEmployeeCode = () => {
    if (sEmployeeCode.trim() === "") {
      setCodeError("Employee Code is a Required Field!");
      return false;
    }
    setCodeError("");
    return true;
  };

  const isValidName = () => {
    if (sName.trim() === "") {
      setNameError("Employee Name is a Required Field!");
      return false;
    }
    setNameError("");
    return true;
  };

  const validate = () => {
    let isValid = true;
    // Run all validations
    if (!isValidEmployeeCode()) isValid = false;
    if (!isValidName()) isValid = false;
    
    // Only Designation, Branch, State, District are optional, no need to validate
    return isValid;
  };

  // API Calls
  const getEmployeeDetails = useCallback(async (id) => {
    dispatch(setLoader(true));
    // Note: We don't need langCode header as employee data is non-translatable
    const result = await apiHandler({
      url: endpoint.GET_EMPLOYEE_BY_ID + id, // Assuming you define this endpoint
      method: "GET",
      authToken: authData.accessToken,
      refreshToken: authData && authData.refreshToken,
      dispatch,
      navigate,
    });
    if (result.data.status === "success" && result.data.data) {
      const employee = result.data.data;
      setIEmployeeId(employee.iEmployeeId);
      setSEmployeeCode(employee.sEmployeeCode || "");
      setSName(employee.sName || "");
      setSDesignation(employee.sDesignation || "");
      setSBranch(employee.sBranch || "");
      setSState(employee.sState || "");
      setSDistrict(employee.sDistrict || "");
      setBEnabled(employee.bEnabled); // <-- FIX: Set bEnabled from fetched data
    } else {
      console.error("getEmployeeDetails : API Error", result.data);
      navigate("/employees", { replace: true }); // Redirect on failure
    }
    dispatch(setLoader(false));
  }, [authData, dispatch, navigate]);

  useEffect(() => {
    if (state && state.employee) {
      let employee = state.employee;
      getEmployeeDetails(employee.iEmployeeId);
    }
  }, [state, getEmployeeDetails]);

  const addEmployee = async (data) => {
    const result = await apiHandler({
      url: endpoint.ADD_EMPLOYEE, // Assuming you define this endpoint
      method: "POST",
      data: data,
      authToken: authData.accessToken,
      refreshToken: authData && authData.refreshToken,
      dispatch,
      navigate,
    });
    return result.data;
  };

  const editEmployee = async (id, data) => {
    const result = await apiHandler({
      url: endpoint.EDIT_EMPLOYEE + id, // Assuming you define this endpoint
      method: "PUT",
      data: data,
      authToken: authData.accessToken,
      refreshToken: authData && authData.refreshToken,
      dispatch,
      navigate,
    });
    return result.data;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }
    dispatch(setLoader(true));

    const payload = {
      sEmployeeCode,
      sName,
      sDesignation: sDesignation || null,
      sBranch: sBranch || null,
      sState: sState || null,
      sDistrict: sDistrict || null,
      bEnabled: bEnabled ? 1 : 0,
    };

    let response;
    if (state && state.employee) {
      // Editing
      response = await editEmployee(iEmployeeId, payload);
    } else {
      // Adding
      response = await addEmployee(payload);
    }

    if (response.status === "success") {
      setResponse(response.message);
    } else {
      setResponse(response.message);
    }

    dispatch(setLoader(false));
  };

  const handleBack = (event) => {
    event.preventDefault();
    navigate(-1, { replace: true });
  };

  return {
    iEmployeeId,
    sEmployeeCode,
    sName,
    sDesignation,
    sBranch,
    sState,
    sDistrict,
    bEnabled,
    setSEmployeeCode,
    setSName,
    setSDesignation,
    setSBranch,
    setSState,
    setSDistrict,
    setBEnabled,
    isValidEmployeeCode,
    isValidName,
    codeError,
    nameError,
    handleSubmit,
    handleBack,
    response,
    openUpdateAlert,
    handleClose,
  };
};

// NOTE: You must update your endpoint.js with the following:
// export const GET_EMPLOYEE_BY_ID = `/employee/getEmployeeById/`;
// export const ADD_EMPLOYEE = `/employee/createEmployee`;
// export const EDIT_EMPLOYEE = `/employee/updateEmployeeById/`;